import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Anchor, Map, Sun } from 'lucide-react';
import { ArchHero } from '../components/ArchHero';
import { LifestyleSlider } from '../components/LifestyleSlider';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.8
  }
};
export function Home() {
  const [communityInterests, setCommunityInterests] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const handleInterestToggle = (interest: string) => {
    setCommunityInterests(prev => prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]);
  };
  const handleCommunitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && communityInterests.length > 0) {
      setSubmitted(true);
      // In a real app, this would send to a backend
    }
  };
  return <div className="w-full" data-id="element-537" data-mp="2vxf4818x30fl">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-538" data-mp="2vvlyqpa1im96">
        <div className="absolute inset-0 z-0" data-id="element-539" data-mp="2r0ondbpag0t4">
          <img src="https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&q=80" alt="Cabo San Lucas Ocean View" className="w-full h-full object-cover" data-id="element-540" data-mp="2mxrzp6kw5eib" />
          <div className="absolute inset-0 bg-navy/40 mix-blend-multiply" data-id="element-541" data-mp="2d5mbm7xrfh8e" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-542" data-mp="288vvjklyglmz">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="text-5xl md:text-7xl font-serif text-white mb-6 leading-tight" data-id="element-543" data-mp="239sro3hpolgl">
            Your Cabo Life <br data-id="element-544" data-mp="1omkjzcnm2y9d" />
            <span className="text-gold italic" data-id="element-545" data-mp="1on3q7w3wfy7k">Starts Here</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="text-lg md:text-xl text-sand/90 font-light mb-10 max-w-2xl mx-auto" data-id="element-546" data-mp="1ankww9fqyrfb">
            More than just purchasing property. We curate the ultimate resort
            destination lifestyle, connecting you with the community, culture,
            and vetted professionals of Los Cabos.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-547" data-mp="2macj4c68c9jl">
            <Link to="/buyers" className="bg-gold text-white px-8 py-4 uppercase tracking-wider text-sm font-medium hover:bg-gold-dark transition-colors" data-id="element-548" data-mp="akmtp2ypb3am">
              Explore Properties
            </Link>
            <Link to="/collective" className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 uppercase tracking-wider text-sm font-medium hover:bg-white/20 transition-colors" data-id="element-549" data-mp="3srkffpswol9b">
              Meet The Collective
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-sand px-4" data-id="element-550" data-mp="1h2jf08lid1u1">
        <div className="max-w-4xl mx-auto text-center" data-id="element-551" data-mp="1h18b88s2bu2x">
          <motion.div {...fadeIn} data-id="element-552" data-mp="1c3ymkvchzmg5">
            <Anchor className="mx-auto text-gold mb-6" size={32} data-id="element-553" data-mp="1c2x2k1di7pma" />
            <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 leading-tight" data-id="element-554" data-mp="27eep88ghsxgv">
              We believe real estate is just the canvas. <br data-id="element-555" data-mp="2cdreezuu6gmm" />
              <span className="italic text-gold" data-id="element-556" data-mp="2cd81xynygkfz">
                The lifestyle is the masterpiece.
              </span>
            </h2>
            <p className="text-lg text-navy/70 leading-relaxed font-light" data-id="element-557" data-mp="2maw2x6c5mo6l">
              Cabo Collection is a lifestyle-first real estate experience. We
              don't just hand you the keys to a house; we hand you the keys to
              the community. From hidden beaches and exclusive golf clubs to the
              most reliable contractors and legal advisors, we are your trusted
              guides to living beautifully in Baja.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Collective Preview */}
      <section className="py-24 bg-white" data-id="element-558" data-mp="3a2b3ouoa8xfq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-559" data-mp="3e224lr7iwtxz">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-560" data-mp="3e08we7h9i1nv">
            <motion.div {...fadeIn} className="order-2 lg:order-1" data-id="element-561" data-mp="12gji29h0xq65">
              <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&q=80" alt="Luxury Cabo Villa" className="w-full h-[600px] object-cover rounded-sm shadow-2xl" data-id="element-562" data-mp="yd4zpc5m4xpj" />
            </motion.div>
            <motion.div {...fadeIn} className="order-1 lg:order-2" data-id="element-563" data-mp="1m3h4ie5kaebw">
              <h4 className="text-gold font-semibold tracking-widest uppercase text-sm mb-4" data-id="element-564" data-mp="1m4s8of2x5o3j">
                Exclusive Network
              </h4>
              <h2 className="text-4xl font-serif text-navy mb-6" data-id="element-565" data-mp="1cce7pojo0lif">
                The Collective
              </h2>
              <p className="text-navy/70 mb-8 leading-relaxed font-light text-lg" data-id="element-566" data-mp="17d9wyth3efpz">
                Building, remodeling, or simply settling into a new country
                shouldn't be stressful. The Collective is our curated directory
                of vetted, elite professionals—from architects and interior
                designers to legal firms and residency consultants.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-567" data-mp="25i7wr4usjxk">
                {['Rigorously vetted through background checks & client testimonials', 'Exclusive pricing and fast-tracked services for our clients', 'Trusted local experts who deliver on their promises'].map((item, i) => <li key={i} className="flex items-start" data-id="element-568" data-mp="3floikq41hzh3" data-mpi={i}>
                    <CheckCircle2 className="text-gold mr-3 shrink-0 mt-1" size={20} data-id="element-569" data-mp="3fn99m5r5b0a7" />
                    <span className="text-navy/80" data-id="element-570" data-mp="36khxs4ucy2i1">{item}</span>
                  </li>)}
              </ul>
              <Link to="/collective" className="inline-flex items-center text-navy font-semibold uppercase tracking-wider hover:text-gold transition-colors group" data-id="element-571" data-mp="zvlgymj5ztx7">
                Explore The Directory
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} data-id="element-572" data-mp="1ev7eg2j0k3ja" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifestyle Slider */}
      <section className="py-24 bg-navy text-sand" data-id="element-573" data-mp="1ixvqej30xqkh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-574" data-mp="1xnjqxi3dsl75">
          <motion.div {...fadeIn} className="text-center" data-id="element-575" data-mp="1xpw8vv952vk7">
            <h2 className="text-4xl md:text-5xl font-serif mb-4 text-white" data-id="element-576" data-mp="1so6xgei08fvl">
              The Cabo Lifestyle
            </h2>
            <p className="text-sand/70 max-w-2xl mx-auto font-light text-lg" data-id="element-577" data-mp="26mna0m2jt0ln">
              Discover the unique rhythms of life in Los Cabos. From world-class
              fairways to hidden coastal gems.
            </p>
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-0 md:px-8" data-id="element-578" data-mp="8a6iphs5ed0r">
          <LifestyleSlider data-id="element-579" data-mp="wu31qjgzia34" />
        </div>
      </section>

      {/* Niche Community Form */}
      <section className="py-24 bg-sand" data-id="element-580" data-mp="ws8q5vrlrubp">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-581" data-mp="11s4md8u26hgp">
          <motion.div {...fadeIn} data-id="element-582" data-mp="16jcvs4ve22tv">
            <Sun className="mx-auto text-gold mb-6" size={32} data-id="element-583" data-mp="16l673xgy5b87" />
            <h2 className="text-3xl md:text-4xl font-serif text-navy mb-6" data-id="element-584" data-mp="1alzsaj308bxe">
              Find Your Tribe
            </h2>
            <p className="text-navy/70 mb-10 font-light text-lg" data-id="element-585" data-mp="dw8xsjupqorl">
              We are actively developing and sourcing properties tailored to
              specific passions. Tell us what kind of community you'd love to be
              part of, and we'll keep you updated.
            </p>

            {submitted ? <div className="bg-white p-8 border border-gold/30 rounded-sm" data-id="element-586" data-mp="rwjmphcgpta4">
                <CheckCircle2 className="mx-auto text-gold mb-4" size={48} data-id="element-587" data-mp="1ya0yhlcpng9n" />
                <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-588" data-mp="239m42emdymvc">
                  Thank You
                </h3>
                <p className="text-navy/70" data-id="element-589" data-mp="2dd0yc9l83z36">
                  We've noted your interests and will be in touch when the
                  perfect community emerges.
                </p>
              </div> : <form onSubmit={handleCommunitySubmit} className="bg-white p-8 md:p-12 shadow-sm text-left" data-id="element-590" data-mp="23mpq4auib3qh">
                <div className="mb-8" data-id="element-591" data-mp="1uo43o4lwz4kf">
                  <label className="block text-sm font-semibold text-navy uppercase tracking-wider mb-4" data-id="element-592" data-mp="1up5oh0sqf5fc">
                    I'm interested in communities centered around:
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-593" data-mp="1kpwy0hx2s8xq">
                    {['Pickleball', 'Golf', 'Art & Design', 'Wellness/Yoga', 'Equestrian', 'LGBTQ+', 'Writing/Creative', 'Surfing', 'Culinary/Wine'].map(interest => <label key={interest} className="flex items-center space-x-3 cursor-pointer group" data-id="element-594" data-mp="8nrkfskg00oi">
                        <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${communityInterests.includes(interest) ? 'bg-gold border-gold' : 'border-navy/30 group-hover:border-gold'}`} data-id="element-595" data-mp="hnxuf4gxmiix">
                          {communityInterests.includes(interest) && <CheckCircle2 size={14} className="text-white" data-id="element-596" data-mp="3oooqyqcr6t7" />}
                        </div>
                        <span className="text-navy/80 font-light" data-id="element-597" data-mp="3v0hsyc6mozo1">
                          {interest}
                        </span>
                      </label>)}
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4" data-id="element-598" data-mp="1hvcpbmobga8v">
                  <input type="email" required placeholder="Your Email Address" value={email} onChange={e => setEmail(e.target.value)} className="flex-grow px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-599" data-mp="1htrxi4tdz5ep" />
                  <button type="submit" className="bg-navy text-white px-8 py-3 uppercase tracking-wider text-sm font-medium hover:bg-navy-light transition-colors whitespace-nowrap" data-id="element-600" data-mp="20n5fl9c03hcw">
                    Keep Me Updated
                  </button>
                </div>
                <p className="text-xs text-navy/50 mt-4 font-light" data-id="element-601" data-mp="g6iq643yiivm">
                  We respect your privacy. No spam, just highly curated updates
                  based on your interests.
                </p>
              </form>}
          </motion.div>
        </div>
      </section>
    </div>;
}